import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Drawer,
  Form,
  Input,
  Modal,
  Pagination,
  Progress,
  Radio,
  Row,
  Segmented,
  Select,
  Space,
  Switch,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import {
  AppstoreOutlined,
  AudioOutlined,
  CheckCircleOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DesktopOutlined,
  FilterOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  InboxOutlined,
  PauseOutlined,
  PlayCircleOutlined,
  ProfileOutlined,
  ReloadOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import { useActiveCustomers } from '@/hooks/useCustomers';
import { useProjectsForSelection } from '@/hooks/useProject';
import type { Customer } from '@/services/customer.service';
import type { Project } from '@/services/project.service';
import { useUsers } from '@/hooks/useUsers';
import { useWorkOrderExecutionBoard } from '@/hooks/useWorkOrderExecutionBoard';
import {
  useCompleteStage,
  useHoldStage,
  usePauseStage,
  useRequestInspection,
  useResumeStage,
  useStartStage,
  useUpdateStage,
  useWorkstations,
} from '@/hooks/useQCWorkflow';
import {
  defaultStageQueueFilters,
  type StageQueueFilters,
  type StageQueueStageRow,
  type StageQueueWorkOrderGroup,
} from '@/services/workOrderExecutionBoard.service';
import { WorkOrderExecutionBadge } from '@/components/manufacturing/WorkOrderExecutionBadge';
import { stageStartAssignmentsRequiredMessage } from '@/utils/stageStartAssignments';
import { canAccessEodClose } from '@/utils/eodClose';
import { isWoOperatorScope } from '@/utils/woOperatorScope';
import { useAuthStore } from '@/store/authStore';
import './wo-operations-board-tv.css';

const { Title, Text, Paragraph } = Typography;

function getFullscreenElement(): Element | null {
  return (
    document.fullscreenElement
    ?? (document as Document & { webkitFullscreenElement?: Element }).webkitFullscreenElement
    ?? null
  );
}

async function requestFullscreen(el: HTMLElement): Promise<void> {
  if (el.requestFullscreen) {
    await el.requestFullscreen();
    return;
  }
  const webkitEl = el as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
  if (webkitEl.webkitRequestFullscreen) {
    await webkitEl.webkitRequestFullscreen();
  }
}

async function exitFullscreen(): Promise<void> {
  if (document.exitFullscreen) {
    await document.exitFullscreen();
    return;
  }
  const webkitDoc = document as Document & { webkitExitFullscreen?: () => Promise<void> | void };
  if (webkitDoc.webkitExitFullscreen) {
    await webkitDoc.webkitExitFullscreen();
  }
}

const WO_STATUS_OPTIONS = [
  'Draft',
  'Released',
  'In Progress',
  'Completed',
  'Cancelled',
  'Closed',
  'Inspection Pending',
];

const STAGE_STATUS_OPTIONS = ['Pending', 'In Progress', 'Paused', 'Completed', 'On Hold', 'Skipped', 'Inspection Pending', 'Inspection Rejected'];

const DENSITY_STORAGE_KEY = 'mfg-wo-ops-density';
const QUICK_FILTER_STORAGE_KEY = 'mfg-wo-ops-quick-filter';
const HIGH_CONTRAST_STORAGE_KEY = 'mfg-wo-ops-high-contrast';
const BOARD_SORT_STORAGE_KEY = 'mfg-wo-ops-board-sort';
const BOARD_GROUP_STORAGE_KEY = 'mfg-wo-ops-board-group';

function formatBoardDataAge(dataUpdatedAt: number | undefined): string {
  if (dataUpdatedAt == null || dataUpdatedAt <= 0) return '—';
  const s = Math.floor((Date.now() - dataUpdatedAt) / 1000);
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  return dayjs(dataUpdatedAt).format('YYYY-MM-DD HH:mm');
}

type TimeFilter = 'all' | 'today' | 'week' | 'month';
type QuickFilter = 'all' | 'urgent' | 'in_progress' | 'my_line';
type BoardDensity = 'compact' | 'standard' | 'detail';
type BoardSort = 'wo_asc' | 'wo_desc' | 'due_asc' | 'due_desc' | 'customer_asc';
type BoardGroup = 'none' | 'wo_status' | 'stage_status' | 'workstation';

function getCurrentUserId(): string {
  try {
    const s = localStorage.getItem('user');
    if (!s) return '';
    const u = JSON.parse(s) as { id?: string };
    return u.id || '';
  } catch {
    return '';
  }
}

/** TaskWOOperationUIUX.md §5 — 8px stripe color (peripheral status awareness). */
function getStatusStripeColor(status: string, isOverdue: boolean): string {
  if (isOverdue) return '#E84B2E';
  switch (status) {
    case 'Pending':
      return '#D97706';
    case 'In Progress':
      return '#2563EB';
    case 'Completed':
      return '#059669';
    case 'Paused':
    case 'On Hold':
      return '#6B7280';
    case 'Skipped':
      return '#7c3aed';
    case 'Inspection Pending':
      return '#FA8C16';
    case 'Inspection Rejected':
      return '#CF1322';
    default:
      return '#d9d9d9';
  }
}

/** TaskWOOperationUIUX.md — mandatory progress bar (0–100) by stage state. */
function stageProgressValue(row: StageQueueStageRow): number {
  switch (row.status) {
    case 'Pending':
      return 8;
    case 'In Progress':
      return 55;
    case 'Paused':
    case 'On Hold':
      return 40;
    case 'Completed':
    case 'Skipped':
      return 100;
    default:
      return 0;
  }
}

function readStoredQuickFilter(): QuickFilter {
  const v = localStorage.getItem(QUICK_FILTER_STORAGE_KEY);
  if (v === 'urgent' || v === 'in_progress' || v === 'my_line' || v === 'all') return v;
  return 'all';
}

function readStoredDensity(): BoardDensity {
  const v = localStorage.getItem(DENSITY_STORAGE_KEY);
  if (v === 'compact' || v === 'standard' || v === 'detail') return v;
  return 'standard';
}

const DENSITY_CYCLE_ORDER: BoardDensity[] = ['compact', 'standard', 'detail'];

function nextBoardDensity(current: BoardDensity): BoardDensity {
  const i = DENSITY_CYCLE_ORDER.indexOf(current);
  const k = i === -1 ? 0 : (i + 1) % DENSITY_CYCLE_ORDER.length;
  return DENSITY_CYCLE_ORDER[k]!;
}

function readStoredBoardSort(): BoardSort {
  const v = localStorage.getItem(BOARD_SORT_STORAGE_KEY);
  if (v === 'wo_asc' || v === 'wo_desc' || v === 'due_asc' || v === 'due_desc' || v === 'customer_asc') return v;
  return 'wo_asc';
}

function readStoredBoardGroup(): BoardGroup {
  const v = localStorage.getItem(BOARD_GROUP_STORAGE_KEY);
  if (v === 'none' || v === 'wo_status' || v === 'stage_status' || v === 'workstation') return v;
  return 'none';
}

function stageStatusColor(status: string): string {
  switch (status) {
    case 'Pending':
      return 'default';
    case 'In Progress':
      return 'processing';
    case 'Paused':
      return 'gold';
    case 'Completed':
      return 'success';
    case 'On Hold':
      return 'warning';
    case 'Skipped':
      return 'purple';
    default:
      return 'default';
  }
}

function stageMatchesTimeFilter(s: StageQueueStageRow, f: TimeFilter): boolean {
  if (f === 'all') return true;
  const ref = (s.completed_date || s.started_date || s.planned_end_date || '').slice(0, 10);
  if (!ref) return true;
  const d = dayjs(ref);
  if (f === 'today') return d.isSame(dayjs(), 'day');
  if (f === 'week') return d.isAfter(dayjs().subtract(7, 'day').startOf('day'));
  if (f === 'month') return d.isSame(dayjs(), 'month');
  return true;
}

function isWorkOrderFullyComplete(wo: StageQueueWorkOrderGroup): boolean {
  if (wo.stages.length === 0) return false;
  return wo.stages.every((s) => s.status === 'Completed' || s.status === 'Skipped');
}

function clientQuickFilterMatch(
  s: StageQueueStageRow,
  wo: StageQueueWorkOrderGroup,
  qf: QuickFilter,
  userId: string
): boolean {
  if (qf === 'all') return true;
  if (qf === 'urgent') {
    if (s.is_overdue) return true;
    if (wo.planned_end_date && dayjs(wo.planned_end_date).isBefore(dayjs().startOf('day'))) {
      if (!['Completed', 'Closed', 'Cancelled'].includes(wo.work_order_status)) {
        if (!['Completed', 'Skipped'].includes(s.status)) return true;
      }
    }
    return false;
  }
  if (qf === 'in_progress') {
    return s.status === 'In Progress' || s.status === 'Paused' || s.status === 'On Hold';
  }
  if (qf === 'my_line') {
    if (userId && s.assigned_to_user_id === userId) return true;
    if (userId && wo.production_supervisor_user_id === userId) return true;
    return false;
  }
  return true;
}

function stageClientSearchMatch(wo: StageQueueWorkOrderGroup, s: StageQueueStageRow, q: string): boolean {
  if (!q) return true;
  const t = (str: string | null | undefined) => (str || '').toLowerCase();
  const pack = [
    t(wo.work_order_number),
    t(wo.customer_name),
    t(wo.project_name),
    t(wo.sales_order_number),
    t(s.stage_code),
    t(s.stage_name),
    t(s.operation_label),
    t(s.line_item_code),
    t(s.line_item_description),
    t(s.workstation_name),
    s.production_unit_number != null && s.production_unit_number !== undefined
      ? t(`unit ${s.production_unit_number}`)
      : t('legacy'),
  ].join(' ');
  return pack.includes(q) || q.split(/\s+/).every((p) => pack.includes(p));
}

type WORow = { wo: StageQueueWorkOrderGroup; stages: StageQueueStageRow[] };

function sortWORows(rows: WORow[], sort: BoardSort): WORow[] {
  const out = [...rows];
  const dueVal = (row: WORow, forAsc: boolean) => {
    if (!row.wo.planned_end_date) return forAsc ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
    return dayjs(row.wo.planned_end_date).valueOf();
  };
  out.sort((a, b) => {
    if (sort === 'wo_asc') {
      return a.wo.work_order_number.localeCompare(b.wo.work_order_number, undefined, { numeric: true });
    }
    if (sort === 'wo_desc') {
      return b.wo.work_order_number.localeCompare(a.wo.work_order_number, undefined, { numeric: true });
    }
    if (sort === 'due_asc') {
      const da = dueVal(a, true);
      const db = dueVal(b, true);
      if (da !== db) return da < db ? -1 : 1;
      return a.wo.work_order_number.localeCompare(b.wo.work_order_number, undefined, { numeric: true });
    }
    if (sort === 'due_desc') {
      const da = dueVal(a, false);
      const db = dueVal(b, false);
      if (da !== db) return da < db ? 1 : -1;
      return a.wo.work_order_number.localeCompare(b.wo.work_order_number, undefined, { numeric: true });
    }
    if (sort === 'customer_asc') {
      const ca = (a.wo.customer_name || '—').toLowerCase();
      const cb = (b.wo.customer_name || '—').toLowerCase();
      if (ca !== cb) return ca.localeCompare(cb);
      return a.wo.work_order_number.localeCompare(b.wo.work_order_number, undefined, { numeric: true });
    }
    return 0;
  });
  return out;
}

function firstDisplayStage(stages: StageQueueStageRow[]): StageQueueStageRow | undefined {
  if (stages.length === 0) return undefined;
  return [...stages].sort((a, b) => {
    const la = a.line_number ?? 999999;
    const lb = b.line_number ?? 999999;
    if (la !== lb) return la - lb;
    const ua = a.production_unit_number ?? 1;
    const ub = b.production_unit_number ?? 1;
    if (ua !== ub) return ua - ub;
    return a.sequence_number - b.sequence_number;
  })[0];
}

function groupKeyForWORow(row: WORow, group: BoardGroup): string {
  if (group === 'none') return 'all';
  if (group === 'wo_status') return row.wo.work_order_status || '—';
  if (group === 'stage_status') {
    return firstDisplayStage(row.stages)?.status || '—';
  }
  if (group === 'workstation') {
    const s = firstDisplayStage(row.stages);
    if (s?.workstation_name) return s.workstation_name;
    if (s?.workstation_code) return s.workstation_code;
    return 'Unassigned';
  }
  return 'all';
}

function groupWORows(rows: WORow[], group: BoardGroup, subSort: BoardSort): { key: string; label: string; items: WORow[] }[] {
  if (group === 'none') {
    return [{ key: 'all', label: '', items: sortWORows(rows, subSort) }];
  }
  const m = new Map<string, WORow[]>();
  for (const r of rows) {
    const k = groupKeyForWORow(r, group);
    if (!m.has(k)) m.set(k, []);
    m.get(k)!.push(r);
  }
  return [...m.entries()]
    .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
    .map(([key, items]) => {
      const label =
        group === 'wo_status'
          ? `Work order: ${key}`
          : group === 'stage_status'
            ? `Operation: ${key}`
            : `Line: ${key}`;
      return { key, label, items: sortWORows(items, subSort) };
    });
}

function isWoCompletedThisCalendarDay(wo: StageQueueWorkOrderGroup): boolean {
  if (!isWorkOrderFullyComplete(wo)) return false;
  const dates = wo.stages
    .map((s) => s.completed_date)
    .filter((d): d is string => Boolean(d));
  if (dates.length === 0) return false;
  const latest = dates.reduce((a, b) => (dayjs(a).isAfter(b) ? a : b), dates[0]);
  return dayjs(latest).isSame(dayjs(), 'day');
}

type PrimaryStageAction = 'start' | 'complete' | 'resume' | null;

function getPrimaryStageAction(st: string, canStart: boolean): PrimaryStageAction {
  if (st === 'Pending' && canStart) return 'start';
  if (st === 'In Progress') return 'complete';
  if (st === 'Paused' || st === 'On Hold' || st === 'Inspection Rejected') return 'resume';
  return null;
}

function makeStageSelectionKey(workOrderId: string, stageId: string): string {
  return `${workOrderId}::${stageId}`;
}

function StageElapsedTimer({ startedIso }: { startedIso: string | null }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [startedIso]);
  if (!startedIso) return null;
  const start = new Date(startedIso).getTime();
  const sec = Math.max(0, Math.floor((Date.now() - start) / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  const hoursDec = (sec / 3600).toFixed(2);
  return (
    <div style={{ textAlign: 'center', marginBottom: 10 }}>
      <div
        style={{
          fontSize: 26,
          fontWeight: 700,
          color: '#1677ff',
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: 0.5,
        }}
      >
        {pad(Math.min(h, 99))}:{pad(m)}:{pad(s)}
      </div>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {hoursDec} hours
      </Text>
    </div>
  );
}

/** Net time in progress from activity log (Start/Resume → Pause/Hold/Complete), else wall clock start→complete. */
function computeCompletedStageTotalSeconds(row: StageQueueStageRow): number | null {
  if (row.status !== 'Completed' || !row.completed_date) return null;
  const log = row.activity_log ?? [];
  if (log.length > 0) {
    const sorted = [...log].sort(
      (a, b) => new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime()
    );
    let totalMs = 0;
    let segmentStart: number | null = null;
    for (const e of sorted) {
      const t = new Date(e.occurred_at).getTime();
      if (Number.isNaN(t)) continue;
      if (e.event_type === 'Start' || e.event_type === 'Resume') {
        segmentStart = t;
      } else if (e.event_type === 'Pause' || e.event_type === 'Hold' || e.event_type === 'Complete') {
        if (segmentStart != null) {
          totalMs += Math.max(0, t - segmentStart);
          segmentStart = null;
        }
      }
    }
    const sec = Math.floor(totalMs / 1000);
    if (sec > 0) return sec;
  }
  if (row.started_date && row.completed_date) {
    const a = new Date(row.started_date).getTime();
    const b = new Date(row.completed_date).getTime();
    if (!Number.isNaN(a) && !Number.isNaN(b) && b >= a) {
      return Math.floor((b - a) / 1000);
    }
  }
  return null;
}

function formatHmsSeconds(sec: number): { hms: string; hoursDec: string } {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    hms: `${pad(Math.min(h, 99))}:${pad(m)}:${pad(s)}`,
    hoursDec: (sec / 3600).toFixed(2),
  };
}

function StageCompletedTotalTime({ row }: { row: StageQueueStageRow }) {
  const sec = computeCompletedStageTotalSeconds(row);
  if (sec == null) return null;
  const { hms, hoursDec } = formatHmsSeconds(sec);
  return (
    <div style={{ textAlign: 'center', marginBottom: 10 }}>
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
        Total time
      </Text>
      <div
        style={{
          fontSize: 24,
          fontWeight: 700,
          color: '#52c41a',
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: 0.5,
        }}
      >
        {hms}
      </div>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {hoursDec} hours
      </Text>
    </div>
  );
}

function OperationStageCard({
  row,
  workOrderId,
  workOrderNumber,
  onRefetch,
  mutations,
  message,
  workstationOptions,
  userOptions,
  updateStage,
  density,
  onOpenOperationDetail,
  selectMode,
  stageSelected,
  onStageSelectChange,
  lockAssignments,
}: {
  row: StageQueueStageRow;
  workOrderId: string;
  workOrderNumber: string;
  onRefetch: () => void;
  mutations: {
    start: ReturnType<typeof useStartStage>;
    complete: ReturnType<typeof useCompleteStage>;
    pause: ReturnType<typeof usePauseStage>;
    hold: ReturnType<typeof useHoldStage>;
    resume: ReturnType<typeof useResumeStage>;
    requestInspection: ReturnType<typeof useRequestInspection>;
  };
  message: ReturnType<typeof App.useApp>['message'];
  workstationOptions: { value: string; label: string; default_operator_user_id?: string | null }[];
  userOptions: { value: string; label: string }[];
  updateStage: ReturnType<typeof useUpdateStage>;
  density: BoardDensity;
  onOpenOperationDetail?: () => void;
  selectMode?: boolean;
  stageSelected?: boolean;
  onStageSelectChange?: (selected: boolean) => void;
  /** WO Operators cannot reassign the operation or open the full work order. */
  lockAssignments?: boolean;
}) {
  const busy =
    mutations.start.isPending ||
    mutations.complete.isPending ||
    mutations.pause.isPending ||
    mutations.hold.isPending ||
    mutations.resume.isPending;

  const st = row.status;
  const running = st === 'In Progress';
  const canStart = st === 'Pending' && row.can_start;
  const inspRequired = Boolean((row as { inspection_required?: unknown }).inspection_required);
  const inspStatus = (row as any).inspection_status as string | null | undefined;
  const canRequestInspection = running && inspRequired;
  const canComplete =
    st === 'In Progress' && !inspRequired
    || (st === 'Inspection Pending' && inspStatus === 'Approved');
  const canPauseOrHold = st === 'In Progress';
  const canResume = st === 'Paused' || st === 'On Hold' || st === 'Inspection Rejected';

  const fire = (fn: () => void) => {
    try {
      fn();
    } catch {
      onRefetch();
    }
  };

  const canSetWorkstation = st === 'Pending' && !lockAssignments;
  const showRunningOnWorkstation =
    Boolean(row.workstation_id) && (st === 'In Progress' || st === 'Paused' || st === 'On Hold');
  const workstationLine =
    row.workstation_code && row.workstation_name
      ? `${row.workstation_code} — ${row.workstation_name}`
      : row.workstation_name || row.workstation_code || row.workstation_id || '';

  const handleWorkstationChange = (workstationId: string | null) => {
    if (!canSetWorkstation) {
      message.warning('Workstation can only be set while the operation is Pending (before Start).');
      return;
    }
    void (async () => {
      try {
        const ws = workstationId
          ? workstationOptions.find((o) => o.value === workstationId)
          : undefined;
        await updateStage.mutateAsync({
          id: row.stage_id,
          workOrderId,
          data: {
            workstation_id: workstationId,
            assigned_to_user_id: ws?.default_operator_user_id ?? null,
          },
        });
      } catch {
        // useUpdateStage shows error toast
      }
    })();
  };

  const handleUserRoleChange = (field: 'assigned_to_user_id' | 'inspector_user_id' | 'verifier_user_id', userId: string | null) => {
    if (!canSetWorkstation) {
      message.warning('Operator, inspector, and verifier can only be set while the operation is Pending (before Start).');
      return;
    }
    void (async () => {
      try {
        await updateStage.mutateAsync({
          id: row.stage_id,
          workOrderId,
          data: { [field]: userId },
        });
      } catch {
        // useUpdateStage shows error toast
      }
    })();
  };

  const stripe = getStatusStripeColor(st, row.is_overdue);
  const barPct = stageProgressValue(row);
  const compact = density === 'compact';
  const detail = density === 'detail';
  const touchBtn = { minHeight: 48, minWidth: 48, padding: '0 16px' as const };
  const primary = getPrimaryStageAction(st, canStart);

  return (
    <Card
      className={row.is_overdue ? 'mfg-wo-ops-card--overdue-pulse' : undefined}
      size="small"
      style={{
        borderRadius: 12,
        boxShadow: row.is_overdue ? '0 2px 12px rgba(232,75,46,0.12)' : '0 2px 8px rgba(0,0,0,0.06)',
        border: '1px solid #f0f0f0',
        borderLeft: `8px solid ${stripe}`,
        background: row.is_overdue ? '#fff5f5' : undefined,
      }}
      styles={{ body: { padding: compact ? 10 : detail ? 20 : 16 } }}
    >
      <div
        style={{ height: 4, background: '#f0f0f0', borderRadius: 2, marginBottom: compact ? 6 : 10 }}
        aria-hidden
      >
        <div
          style={{
            width: `${barPct}%`,
            height: '100%',
            borderRadius: 2,
            background: stripe,
            transition: 'width 0.35s ease',
          }}
        />
      </div>
      <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <div style={{ flex: 1, minWidth: 200 }}>
          <Space size={8} wrap align="center">
            {selectMode ? (
              <Checkbox
                checked={!!stageSelected}
                onChange={(e) => onStageSelectChange?.(e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                aria-label="Select this operation for bulk actions"
                style={{ transform: 'scale(1.15)' }}
              />
            ) : null}
            <ClockCircleOutlined style={{ color: '#1677ff' }} />
            <Text strong style={{ fontSize: 15 }}>
              {row.operation_label}
            </Text>
            <Tag color={stageStatusColor(st)}>{st}</Tag>
            {st === 'Pending' && !row.can_start && !row.iir_approved && (
              <Tooltip
                title={
                  row.iir_status
                    ? `Job Inspection status: ${row.iir_status}. Approve Job Inspection to start operations.`
                    : 'No Job Inspection found or Job Inspection is not approved yet.'
                }
              >
                <Tag color="red">Awaiting Job Inspection</Tag>
              </Tooltip>
            )}
            {st === 'Pending' && !row.can_start && row.iir_approved && (
              <Tooltip
                title={
                  row.wait_for_previous === false
                    ? 'Refresh the board if this persists — Wait for previous is off for this stage.'
                    : 'The prior operation on this work order line (previous sequence) must be Completed before this one can start. You can disable “Wait for previous” on the stage to allow parallel starts.'
                }
              >
                <Tag color="orange">Waiting on prior operation</Tag>
              </Tooltip>
            )}
            {row.is_overdue && <Tag color="orange">Overdue</Tag>}
            {onOpenOperationDetail ? (
              <Button
                type="link"
                size="small"
                icon={<ProfileOutlined />}
                onClick={() => onOpenOperationDetail()}
                aria-label="Open work order summary in side panel"
              >
                Summary
              </Button>
            ) : null}
          </Space>
          {detail && (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
              {row.planned_end_date
                ? `Due ${dayjs(row.planned_end_date).format('YYYY-MM-DD HH:mm')} · `
                : ''}
              {row.estimated_hours != null
                ? `Est. ${row.estimated_hours} hr${row.estimated_hours !== 1 ? 's' : ''} · `
                : ''}
              {row.assigned_to_user_id || row.operator_name
                ? `Operator: ${row.operator_name || '—'}`
                : 'Operator: —'}
            </Text>
          )}
          <Paragraph type="secondary" style={{ margin: '8px 0 0', fontSize: 12 }} ellipsis={{ rows: 2 }}>
            {lockAssignments ? (
              <Text>{workOrderNumber}</Text>
            ) : (
              <Link to={`/mfg/work-orders/${workOrderId}`}>{workOrderNumber}</Link>
            )}
            {row.line_number != null || row.line_item_description
              ? ` · Line ${row.line_number ?? '—'} · ${row.line_item_description ?? ''}`
              : null}
          </Paragraph>
          {row.planned_end_date && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Planned end: {dayjs(row.planned_end_date).format('YYYY-MM-DD')}
            </Text>
          )}
          {row.estimated_hours != null && (
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              Est. run time: {row.estimated_hours} hr{row.estimated_hours !== 1 ? 's' : ''}
            </Text>
          )}
          {canSetWorkstation && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ marginTop: 10, maxWidth: 360 }}
            >
              <div>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                  Workstation
                </Text>
                <Select
                  showSearch
                  allowClear
                  placeholder="Select workstation"
                  optionFilterProp="label"
                  style={{ width: '100%' }}
                  getPopupContainer={(n) => n?.parentElement ?? document.body}
                  value={row.workstation_id || undefined}
                  options={workstationOptions}
                  loading={updateStage.isPending}
                  disabled={busy || updateStage.isPending}
                  onChange={(v) => handleWorkstationChange(v != null ? String(v) : null)}
                />
              </div>
              <div style={{ marginTop: 10 }}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                  Operator
                </Text>
                <Select
                  showSearch
                  allowClear
                  placeholder="Select operator"
                  optionFilterProp="label"
                  style={{ width: '100%' }}
                  getPopupContainer={(n) => n?.parentElement ?? document.body}
                  value={row.assigned_to_user_id || undefined}
                  options={userOptions}
                  loading={updateStage.isPending}
                  disabled={busy || updateStage.isPending}
                  onChange={(v) => handleUserRoleChange('assigned_to_user_id', v != null ? String(v) : null)}
                />
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8, marginBottom: 4 }}>
                  Inspector
                </Text>
                <Select
                  showSearch
                  allowClear
                  placeholder="Select inspector"
                  optionFilterProp="label"
                  style={{ width: '100%' }}
                  getPopupContainer={(n) => n?.parentElement ?? document.body}
                  value={row.inspector_user_id || undefined}
                  options={userOptions}
                  loading={updateStage.isPending}
                  disabled={busy || updateStage.isPending}
                  onChange={(v) => handleUserRoleChange('inspector_user_id', v != null ? String(v) : null)}
                />
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8, marginBottom: 4 }}>
                  Verifier
                </Text>
                <Select
                  showSearch
                  allowClear
                  placeholder="Select verifier"
                  optionFilterProp="label"
                  style={{ width: '100%' }}
                  getPopupContainer={(n) => n?.parentElement ?? document.body}
                  value={row.verifier_user_id || undefined}
                  options={userOptions}
                  loading={updateStage.isPending}
                  disabled={busy || updateStage.isPending}
                  onChange={(v) => handleUserRoleChange('verifier_user_id', v != null ? String(v) : null)}
                />
              </div>
            </div>
          )}
          {!canSetWorkstation && (row.assigned_to_user_id || row.operator_name || row.inspector_user_id || row.inspector_name || row.verifier_user_id || row.verifier_name) && (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              {row.assigned_to_user_id || row.operator_name ? (
                <div>
                  <Text type="secondary">Operator: </Text>
                  <Text>{row.operator_name || 'Unknown user'}</Text>
                </div>
              ) : null}
              {row.inspector_user_id || row.inspector_name ? (
                <div>
                  <Text type="secondary">Inspector: </Text>
                  <Text>{row.inspector_name || 'Unknown user'}</Text>
                </div>
              ) : null}
              {row.verifier_user_id || row.verifier_name ? (
                <div>
                  <Text type="secondary">Verifier: </Text>
                  <Text>{row.verifier_name || 'Unknown user'}</Text>
                </div>
              ) : null}
            </div>
          )}
          {showRunningOnWorkstation && workstationLine && (
            <Text strong style={{ display: 'block', marginTop: 10, fontSize: 14 }}>
              Running on {workstationLine}
            </Text>
          )}
        </div>
        <div style={{ minWidth: 200, maxWidth: 280 }}>
          {running && <StageElapsedTimer startedIso={row.started_date} />}
          {st === 'Completed' && <StageCompletedTotalTime row={row} />}
          {(st === 'Paused' || st === 'On Hold') && (
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <Tag color={st === 'Paused' ? 'gold' : 'orange'} icon={st === 'Paused' ? <PauseOutlined /> : <InboxOutlined />}>
                {st === 'Paused' ? 'Paused' : 'On hold'}
              </Tag>
            </div>
          )}
        </div>
      </Space>

      <Space wrap style={{ marginTop: 12 }} size={[8, 8]}>
        {/* TaskWOOperationUIUX.md §7–8: primary CTA on surface; 48px min touch targets. */}
        <Button
          type="default"
          size="large"
          className={primary === 'start' ? 'mfg-wo-ops-primary-cta' : undefined}
          icon={<PlayCircleOutlined />}
          disabled={!canStart || busy}
          loading={mutations.start.isPending}
          style={
            {
              ...touchBtn,
              ...(canStart && !busy
                ? { background: '#1677ff', borderColor: '#0958d9', color: '#fff' }
                : { background: '#fafafa', borderColor: '#d9d9d9', color: 'rgba(0,0,0,0.35)' }),
            }
          }
          onClick={() => {
            const assignmentMsg = stageStartAssignmentsRequiredMessage({
              workstation_id: row.workstation_id,
              assigned_to_user_id: row.assigned_to_user_id,
              inspector_user_id: row.inspector_user_id,
              verifier_user_id: row.verifier_user_id,
            });
            if (assignmentMsg) {
              message.error(assignmentMsg);
              return;
            }
            fire(() =>
              mutations.start.mutate(
                { id: row.stage_id },
                {
                  onError: () => {
                    message.warning('Start rejected — refreshing');
                    onRefetch();
                  },
                }
              )
            );
          }}
        >
          Start
        </Button>
        <Button
          type="default"
          size="large"
          icon={<PauseOutlined />}
          disabled={!canPauseOrHold || busy}
          loading={mutations.pause.isPending}
          style={
            {
              ...touchBtn,
              ...(running && canPauseOrHold && !busy
                ? { background: '#fa8c16', borderColor: '#d46b08', color: '#fff' }
                : { background: '#fafafa', borderColor: '#d9d9d9', color: 'rgba(0,0,0,0.35)' }),
            }
          }
          onClick={() =>
            fire(() =>
              mutations.pause.mutate(
                { id: row.stage_id },
                {
                  onError: () => {
                    message.warning('Pause rejected — refreshing');
                    onRefetch();
                  },
                }
              )
            )
          }
        >
          Pause
        </Button>
        <Button
          type="default"
          size="large"
          icon={<InboxOutlined />}
          disabled={!canPauseOrHold || busy}
          loading={mutations.hold.isPending}
          style={
            {
              ...touchBtn,
              ...(running && canPauseOrHold && !busy
                ? { background: '#ff4d4f', borderColor: '#cf1322', color: '#fff' }
                : { background: '#fafafa', borderColor: '#d9d9d9', color: 'rgba(0,0,0,0.35)' }),
            }
          }
          onClick={() =>
            fire(() =>
              mutations.hold.mutate(
                { id: row.stage_id },
                {
                  onError: () => {
                    message.warning('Hold rejected — refreshing');
                    onRefetch();
                  },
                }
              )
            )
          }
        >
          Hold
        </Button>
        <Button
          type="default"
          size="large"
          className={primary === 'resume' ? 'mfg-wo-ops-primary-cta' : undefined}
          icon={<PlayCircleOutlined />}
          disabled={!canResume || busy}
          loading={mutations.resume.isPending}
          style={
            {
              ...touchBtn,
              ...(canResume && !busy
                ? { background: '#13c2c2', borderColor: '#006d75', color: '#fff' }
                : { background: '#fafafa', borderColor: '#d9d9d9', color: 'rgba(0,0,0,0.35)' }),
            }
          }
          onClick={() =>
            fire(() =>
              mutations.resume.mutate(
                { id: row.stage_id },
                {
                  onError: () => {
                    message.warning('Resume rejected — refreshing');
                    onRefetch();
                  },
                }
              )
            )
          }
        >
          Resume
        </Button>
        {canRequestInspection && (
          <Button
            type="default"
            size="large"
            icon={<CheckSquareOutlined />}
            disabled={busy}
            loading={mutations.requestInspection.isPending}
            style={{ ...touchBtn, background: '#faad14', borderColor: '#d48806', color: '#fff' }}
            onClick={() =>
              fire(() =>
                mutations.requestInspection.mutate(
                  { id: row.stage_id },
                  {
                    onError: () => {
                      message.warning('Request rejected — refreshing');
                      onRefetch();
                    },
                  }
                )
              )
            }
          >
            Request Inspection
          </Button>
        )}
        {st === 'Inspection Pending' && inspStatus !== 'Approved' && (
          <Tag color="orange" style={{ fontSize: 13, padding: '4px 10px' }}>Waiting Inspection</Tag>
        )}
        {st === 'Inspection Pending' && inspStatus === 'Approved' && (
          <Tag color="green" style={{ fontSize: 13, padding: '4px 10px' }}>Inspection Approved</Tag>
        )}
        {st === 'Inspection Rejected' && (
          <div style={{ marginBottom: 4 }}>
            <Tag color="red" style={{ fontSize: 13, padding: '4px 10px' }}>Inspection Rejected – Rework Required</Tag>
            {row.notes && (() => {
              const line = row.notes!.split('\n').filter((l: string) => l.includes('[Inspection Rejected]')).pop();
              const reason = line?.replace('[Inspection Rejected]', '').trim();
              return reason ? (
                <div style={{ marginTop: 4, padding: '4px 8px', background: '#fff2f0', borderRadius: 4, fontSize: 12 }}>
                  <Text type="danger" style={{ fontSize: 12 }}><strong>Reason:</strong> {reason}</Text>
                </div>
              ) : null;
            })()}
          </div>
        )}
        <Button
          type="default"
          size="large"
          className={primary === 'complete' ? 'mfg-wo-ops-primary-cta' : undefined}
          icon={<CheckCircleOutlined />}
          disabled={!canComplete || busy}
          loading={mutations.complete.isPending}
          style={
            {
              ...touchBtn,
              ...(canComplete && !busy
                ? { background: '#52c41a', borderColor: '#237804', color: '#fff' }
                : { background: '#fafafa', borderColor: '#d9d9d9', color: 'rgba(0,0,0,0.35)' }),
            }
          }
          onClick={() =>
            fire(() =>
              mutations.complete.mutate(
                { id: row.stage_id },
                {
                  onError: () => {
                    message.warning('Complete rejected — refreshing');
                    onRefetch();
                  },
                }
              )
            )
          }
        >
          Complete
        </Button>
      </Space>
      {!compact && (row.activity_log?.length ?? 0) > 0 && (
        <div style={{ marginTop: 14 }} onClick={(e) => e.stopPropagation()}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Activity log
          </Text>
          <Timeline
            style={{ marginTop: 8 }}
            items={(row.activity_log ?? []).map((e) => ({
              color:
                e.event_type === 'Start'
                  ? 'blue'
                  : e.event_type === 'Complete'
                    ? 'green'
                    : e.event_type === 'Pause'
                      ? 'gold'
                      : e.event_type === 'Hold'
                        ? 'orange'
                        : e.event_type === 'Inspection_Request'
                          ? 'volcano'
                          : e.event_type === 'Inspection_Release'
                            ? 'lime'
                            : 'cyan',
              children: (
                <div>
                  <Text strong style={{ fontSize: 13 }}>
                    {e.event_type}
                  </Text>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {e.occurred_at ? dayjs(e.occurred_at).format('YYYY-MM-DD HH:mm:ss') : '—'}
                    </Text>
                  </div>
                  {e.notes ? (
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                      {e.notes}
                    </Text>
                  ) : null}
                </div>
              ),
            }))}
          />
        </div>
      )}
    </Card>
  );
}

const WorkOrderOperationsBoardPage: React.FC = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const tvMode = searchParams.get('mode') === 'tv' || searchParams.get('mode') === 'TV';
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { message } = App.useApp();
  const navigate = useNavigate();
  const authUser = useAuthStore((s) => s.user);
  const canEodClose = canAccessEodClose(
    authUser?.permissions,
    Boolean(authUser?.is_super_admin)
  );
  const operatorScope = isWoOperatorScope(authUser);
  const [form] = Form.useForm();
  const [filters, setFilters] = useState<StageQueueFilters>(defaultStageQueueFilters);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');

  const { data, isLoading, isFetching, refetch, dataUpdatedAt } = useWorkOrderExecutionBoard(filters);
  const [density, setDensity] = useState<BoardDensity>(readStoredDensity);
  const [quickFilter, setQuickFilter] = useState<QuickFilter>(readStoredQuickFilter);
  const [clientSearch, setClientSearch] = useState('');
  const [boardLineFilter, setBoardLineFilter] = useState('__all__');
  const [boardUnitFilter, setBoardUnitFilter] = useState('__all__');
  const [drawer, setDrawer] = useState<{
    open: boolean;
    wo: StageQueueWorkOrderGroup | null;
    focusStageId: string | null;
  }>({ open: false, wo: null, focusStageId: null });
  const [highContrast, setHighContrast] = useState(() => {
    try {
      return localStorage.getItem(HIGH_CONTRAST_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [boardSort, setBoardSort] = useState<BoardSort>(readStoredBoardSort);
  const [boardGroup, setBoardGroup] = useState<BoardGroup>(readStoredBoardGroup);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedStageKeys, setSelectedStageKeys] = useState<Set<string>>(() => new Set());
  const [drawerViewportW, setDrawerViewportW] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth : 1024)
  );

  /**
   * Set true immediately before any user-driven server refetch (Search, More filters, pagination,
   * Refresh, pull-to-refresh, card error refresh, bulk start). Left false for the 60s background
   * refetch so the quiet "Board data updated" toast only runs after auto-refresh.
   */
  const userInitiatedServerFetch = useRef(false);
  const hasCompletedInitialFetch = useRef(false);
  const wasBoardFetching = useRef(false);

  useEffect(() => {
    if (isFetching) wasBoardFetching.current = true;
    if (!isFetching && !isLoading && wasBoardFetching.current) {
      wasBoardFetching.current = false;
      if (!hasCompletedInitialFetch.current) {
        hasCompletedInitialFetch.current = true;
        userInitiatedServerFetch.current = false;
        return;
      }
      if (userInitiatedServerFetch.current) {
        userInitiatedServerFetch.current = false;
        return;
      }
      message.open({
        type: 'info',
        duration: 1.2,
        content: 'Board data updated',
        key: 'mfg-wo-ops-quiet-refresh',
      });
    }
  }, [isFetching, isLoading, message]);

  useEffect(() => {
    const onResize = () => setDrawerViewportW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const startClientSearchVoice = () => {
    const w = window as unknown as {
      webkitSpeechRecognition?: new () => { lang: string; onresult: ((e: Event) => void) | null; onerror: (() => void) | null; start: () => void };
      SpeechRecognition?: new () => { lang: string; onresult: ((e: Event) => void) | null; onerror: (() => void) | null; start: () => void };
    };
    const Ctor = w.webkitSpeechRecognition || w.SpeechRecognition;
    if (!Ctor) {
      message.warning('Voice input is not supported in this browser');
      return;
    }
    const r = new Ctor();
    r.lang = typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US';
    (r as { onresult: (e: unknown) => void }).onresult = (e) => {
      const ev = e as { results: Array<{ 0: { transcript: string } }> };
      const t = ev.results[0][0].transcript?.trim() ?? '';
      setClientSearch(t);
    };
    (r as { onerror: () => void }).onerror = () => message.error('Voice input failed');
    r.start();
  };

  useEffect(() => {
    localStorage.setItem(DENSITY_STORAGE_KEY, density);
  }, [density]);
  useEffect(() => {
    localStorage.setItem(QUICK_FILTER_STORAGE_KEY, quickFilter);
  }, [quickFilter]);
  useEffect(() => {
    try {
      localStorage.setItem(HIGH_CONTRAST_STORAGE_KEY, highContrast ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [highContrast]);
  useEffect(() => {
    try {
      localStorage.setItem(BOARD_SORT_STORAGE_KEY, boardSort);
    } catch {
      /* ignore */
    }
  }, [boardSort]);
  useEffect(() => {
    try {
      localStorage.setItem(BOARD_GROUP_STORAGE_KEY, boardGroup);
    } catch {
      /* ignore */
    }
  }, [boardGroup]);

  useEffect(() => {
    let startY = 0;
    let active = false;
    const threshold = 88;
    const onStart = (e: TouchEvent) => {
      if (window.scrollY > 8) return;
      startY = e.touches[0].clientY;
      active = true;
    };
    const onEnd = (e: TouchEvent) => {
      if (!active) return;
      active = false;
      const endY = e.changedTouches[0].clientY;
      const dy = endY - startY;
      if (window.scrollY <= 8 && dy > threshold) {
        userInitiatedServerFetch.current = true;
        void refetch();
        try {
          if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
            navigator.vibrate(12);
          }
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchend', onEnd);
    };
  }, [refetch]);

  const startStage = useStartStage();
  const completeStage = useCompleteStage();
  const pauseStage = usePauseStage();
  const holdStage = useHoldStage();
  const resumeStage = useResumeStage();
  const requestInspection = useRequestInspection();
  const updateStage = useUpdateStage();
  const { data: workstations = [] } = useWorkstations(true);

  const mutations = useMemo(
    () => ({
      start: startStage,
      complete: completeStage,
      pause: pauseStage,
      hold: holdStage,
      resume: resumeStage,
      requestInspection,
    }),
    [startStage, completeStage, pauseStage, holdStage, resumeStage, requestInspection]
  );

  const workstationFilterOptions = useMemo(
    () => [
      { value: '__unassigned__', label: 'Unassigned (no workstation)' },
      ...(
        (workstations as { id: string; workstation_code: string; workstation_name: string }[]).map((ws) => ({
          value: ws.id,
          label: `${ws.workstation_code} — ${ws.workstation_name}`,
        }))
      ),
    ],
    [workstations]
  );

  const operationCardWorkstationOptions = useMemo(
    () =>
      (
        workstations as {
          id: string;
          workstation_code: string;
          workstation_name: string;
          default_operator_user_id?: string | null;
        }[]
      ).map((ws) => ({
        value: ws.id,
        label: `${ws.workstation_code} — ${ws.workstation_name}`,
        default_operator_user_id: ws.default_operator_user_id ?? null,
      })),
    [workstations]
  );

  const { data: customersData, isLoading: customersLoading } = useActiveCustomers({
    enabled: !operatorScope,
  });
  const customers: Customer[] = customersData ?? [];
  const { data: projectsData, isLoading: projectsLoading } = useProjectsForSelection({
    enabled: !operatorScope,
  });
  const projects: Project[] = projectsData ?? [];
  const { data: users = [], isLoading: usersLoading } = useUsers(
    { is_active: true },
    { enabled: !operatorScope }
  );

  const customerOptions = useMemo(
    () =>
      customers.map((c) => ({
        value: c.id,
        label: c.customer_name || c.id,
      })),
    [customers]
  );

  const projectOptions = useMemo(
    () =>
      projects.map((p) => ({
        value: p.id,
        label: p.project_name || p.id,
      })),
    [projects]
  );

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        label: [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.username || u.email || 'Unknown user',
      })),
    [users]
  );

  const boardUserSelectOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        label: `${[u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.username || u.email || 'Unknown user'}${
          u.email ? ` (${u.email})` : ''
        }`,
      })),
    [users]
  );

  const boardLineFilterOptions = useMemo(() => {
    const groups = data?.work_orders ?? [];
    const m = new Map<string, string>();
    for (const wo of groups) {
      for (const s of wo.stages) {
        const lid = s.work_order_line_item_id;
        if (!lid) continue;
        const ln = s.line_number != null ? `L${s.line_number}` : 'Line';
        const ic = (s.line_item_code && String(s.line_item_code).trim()) || `${lid.slice(0, 8)}…`;
        const label = `${wo.work_order_number} · ${ln} ${ic}`;
        if (!m.has(lid)) m.set(lid, label);
      }
    }
    return [{ value: '__all__', label: 'All WO lines' }, ...[...m.entries()].map(([value, label]) => ({ value, label }))];
  }, [data]);

  const boardUnitFilterOptions = useMemo(() => {
    const groups = data?.work_orders ?? [];
    let legacy = false;
    const nums = new Set<number>();
    for (const wo of groups) {
      for (const s of wo.stages) {
        if (s.production_unit_number == null || s.production_unit_number === undefined) legacy = true;
        else nums.add(Number(s.production_unit_number));
      }
    }
    const out: { value: string; label: string }[] = [{ value: '__all__', label: 'All units' }];
    if (legacy) out.push({ value: '__legacy__', label: 'Legacy' });
    for (const n of [...nums].sort((a, b) => a - b)) {
      out.push({ value: String(n), label: `Unit ${n}` });
    }
    return out;
  }, [data]);

  const showBoardUnitFilter = useMemo(
    () => boardUnitFilterOptions.some((o) => o.value !== '__all__' && o.value !== '__legacy__'),
    [boardUnitFilterOptions]
  );

  const onApplyFilters = (values: Record<string, unknown>) => {
    userInitiatedServerFetch.current = true;
    const dueBefore = values.due_before as Dayjs | undefined;
    const dueAfter = values.due_after as Dayjs | undefined;
    setFilters({
      ...defaultStageQueueFilters(),
      page: 1,
      page_size: filters.page_size,
      include_completed_wo: Boolean(values.include_completed_wo),
      hide_skipped: Boolean(values.hide_skipped),
      only_actionable: Boolean(values.only_actionable),
      work_order_status: (values.work_order_status as string) || undefined,
      stage_status: (values.stage_status as string) || undefined,
      customer_id: (values.customer_id as string) || undefined,
      project_id: (values.project_id as string) || undefined,
      supervisor_user_id: (values.supervisor_user_id as string) || undefined,
      qe_user_id: (values.qe_user_id as string) || undefined,
      workstation_id: (values.workstation_id as string) || undefined,
      search: (values.search as string) || undefined,
      due_before: dueBefore ? dueBefore.format('YYYY-MM-DD') : undefined,
      due_after: dueAfter ? dueAfter.format('YYYY-MM-DD') : undefined,
    });
  };

  const currentUserId = useMemo(() => getCurrentUserId(), []);

  const { activeRows, completedRows, shiftProgressPercent, boardMetrics, listFilteredPill } = useMemo(() => {
    const groups = data?.work_orders ?? [];
    const q = clientSearch.trim().toLowerCase();
    let running = 0;
    let paused = 0;
    let onHold = 0;
    let pending = 0;
    let visibleWo = 0;
    let tot = 0;
    let done = 0;
    const active: { wo: StageQueueWorkOrderGroup; stages: StageQueueStageRow[] }[] = [];
    const completed: { wo: StageQueueWorkOrderGroup; stages: StageQueueStageRow[] }[] = [];
    for (const wo of groups) {
      const displayStages = wo.stages
        .filter((s) => stageMatchesTimeFilter(s, timeFilter))
        .filter((s) => clientQuickFilterMatch(s, wo, quickFilter, currentUserId))
        .filter((s) => {
          if (boardLineFilter !== '__all__' && s.work_order_line_item_id !== boardLineFilter) return false;
          if (boardUnitFilter === '__all__') return true;
          if (boardUnitFilter === '__legacy__')
            return s.production_unit_number == null || s.production_unit_number === undefined;
          return Number(s.production_unit_number) === Number(boardUnitFilter);
        })
        .filter((s) => stageClientSearchMatch(wo, s, q));
      for (const s of displayStages) {
        tot++;
        if (s.status === 'Completed' || s.status === 'Skipped') done++;
        if (s.status === 'In Progress') running++;
        else if (s.status === 'Paused') paused++;
        else if (s.status === 'On Hold') onHold++;
        else if (s.status === 'Pending') pending++;
      }
      if (displayStages.length === 0) continue;
      visibleWo += 1;
      if (isWorkOrderFullyComplete(wo)) {
        completed.push({ wo, stages: displayStages });
      } else {
        active.push({ wo, stages: displayStages });
      }
    }
    const shiftProgressPercent = tot > 0 ? Math.round((done / tot) * 100) : 0;
    const listFilteredPill =
      q.length > 0 || quickFilter !== 'all' || boardLineFilter !== '__all__' || boardUnitFilter !== '__all__';
    return {
      activeRows: active,
      completedRows: completed,
      shiftProgressPercent,
      listFilteredPill,
      boardMetrics: { visibleWo, running, paused, onHold, pending },
    };
  }, [data, timeFilter, quickFilter, clientSearch, currentUserId, boardLineFilter, boardUnitFilter]);

  const { activeGroupSections, completedGroupSections, completedTodayCount } = useMemo(() => {
    const sa = sortWORows(activeRows, boardSort);
    const sc = sortWORows(completedRows, boardSort);
    const today = completedRows.filter(({ wo }) => isWoCompletedThisCalendarDay(wo)).length;
    return {
      activeGroupSections: groupWORows(sa, boardGroup, boardSort),
      completedGroupSections: groupWORows(sc, boardGroup, boardSort),
      completedTodayCount: today,
    };
  }, [activeRows, completedRows, boardSort, boardGroup]);

  const runBulkStartSelected = () => {
    const toStart: string[] = [];
    for (const k of selectedStageKeys) {
      const [woId, stageId] = k.split('::');
      if (!woId || !stageId) continue;
      const wo = data?.work_orders?.find((w) => w.work_order_id === woId);
      const row = wo?.stages.find((s) => s.stage_id === stageId);
      if (row && row.status === 'Pending' && row.can_start) toStart.push(stageId);
    }
    if (toStart.length === 0) {
      message.info('No selected operations can be started (must be Pending and not blocked).');
      return;
    }
    Modal.confirm({
      title: 'Start multiple operations',
      content: `Start ${toStart.length} operation(s) with one Start each? This runs in order until all succeed or one fails.`,
      okText: 'Start all',
      onOk: async () => {
        for (const id of toStart) {
          try {
            await mutations.start.mutateAsync({ id });
          } catch {
            userInitiatedServerFetch.current = true;
            void refetch();
            return;
          }
        }
        setSelectedStageKeys(new Set());
        userInitiatedServerFetch.current = true;
        void refetch();
        message.success(`Started ${toStart.length} operation(s)`);
      },
    });
  };

  const copySelectedWONumbers = () => {
    const nums = new Set<string>();
    for (const k of selectedStageKeys) {
      const [woId] = k.split('::');
      if (!woId) continue;
      const wo = data?.work_orders?.find((w) => w.work_order_id === woId);
      if (wo) nums.add(wo.work_order_number);
    }
    if (nums.size === 0) {
      message.info('Nothing to copy');
      return;
    }
    void navigator.clipboard.writeText([...nums].join('\n'));
    message.success('Work order number(s) copied');
  };

  const renderWOCard = (wo: StageQueueWorkOrderGroup, stages: StageQueueStageRow[]) => (
    <Card
      style={{ borderRadius: 12, border: '1px solid #f0f0f0' }}
      title={
        <Space wrap align="center">
          {operatorScope ? (
            <Text strong style={{ fontSize: 16 }}>
              {wo.work_order_number}
            </Text>
          ) : (
            <Link to={`/mfg/work-orders/${wo.work_order_id}`}>
              <Text strong style={{ fontSize: 16 }}>
                {wo.work_order_number}
              </Text>
            </Link>
          )}
          <Tag>{wo.work_order_status === 'IIR Pending' ? 'Job Inspection Pending' : wo.work_order_status}</Tag>
          <WorkOrderExecutionBadge executionScope={wo.execution_scope} size="compact" />
          {wo.customer_name && <Text type="secondary">{wo.customer_name}</Text>}
          {wo.project_name && <Text type="secondary">· {wo.project_name}</Text>}
        </Space>
      }
      extra={
        <Space direction="vertical" align="end" size={0}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Job Inspection: {wo.iir_status ?? '—'}
            {!wo.iir_approved && wo.iir_status && <Tag color="red">Not approved</Tag>}
          </Text>
          <Progress percent={wo.progress_percent} size="small" style={{ minWidth: 160 }} />
          <Text type="secondary" style={{ fontSize: 11 }}>
            {wo.stages_completed}/{wo.stages_total} done · {wo.stages_in_progress} running · {wo.stages_paused ?? 0} paused · {wo.stages_on_hold ?? 0}{' '}
            hold
          </Text>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {stages.map((row) => (
          <OperationStageCard
            key={row.stage_id}
            row={row}
            workOrderId={wo.work_order_id}
            workOrderNumber={wo.work_order_number}
            onRefetch={() => {
              userInitiatedServerFetch.current = true;
              void refetch();
            }}
            mutations={mutations}
            message={message}
            workstationOptions={operationCardWorkstationOptions}
            userOptions={boardUserSelectOptions}
            updateStage={updateStage}
            density={density}
            selectMode={selectMode}
            stageSelected={selectedStageKeys.has(makeStageSelectionKey(wo.work_order_id, row.stage_id))}
            onStageSelectChange={(sel) => {
              const k = makeStageSelectionKey(wo.work_order_id, row.stage_id);
              setSelectedStageKeys((prev) => {
                const n = new Set(prev);
                if (sel) n.add(k);
                else n.delete(k);
                return n;
              });
            }}
            onOpenOperationDetail={() =>
              setDrawer({ open: true, wo, focusStageId: row.stage_id })
            }
            lockAssignments={operatorScope}
          />
        ))}
      </Space>
    </Card>
  );

  const drawerNarrow = drawerViewportW < 768;

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(getFullscreenElement() === pageRef.current);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
    };
  }, []);

  const effectiveTvMode = tvMode;

  const isExpandedLayout = tvMode || isFullscreen;

  const toggleFullscreen = useCallback(async () => {
    const el = pageRef.current;
    if (!el) return;
    try {
      if (getFullscreenElement() === el) {
        await exitFullscreen();
      } else {
        await requestFullscreen(el);
      }
    } catch {
      // Browser may block fullscreen without user gesture.
    }
  }, []);

  const toggleTv = () => {
    const next = new URLSearchParams(searchParams);
    if (tvMode) next.delete('mode');
    else next.set('mode', 'tv');
    setSearchParams(next);
  };

  const pageClassName = useMemo(() => {
    const parts = ['mfg-wo-ops-page'];
    if (highContrast) parts.push('mfg-wo-ops--hc');
    if (effectiveTvMode) parts.push('mfg-wo-ops-page--tv');
    if (isFullscreen) parts.push('mfg-wo-ops-page--fullscreen');
    return parts.join(' ');
  }, [highContrast, effectiveTvMode, isFullscreen]);

  return (
    <div
      ref={pageRef}
      className={pageClassName}
      style={{
        padding: isExpandedLayout ? 16 : 24,
        paddingBottom: selectMode ? 100 : isExpandedLayout ? 16 : 24,
        maxWidth: isExpandedLayout ? undefined : 1600,
        margin: isExpandedLayout ? 0 : '0 auto',
      }}
    >
      <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} md={16}>
          <Title level={effectiveTvMode ? 2 : 3} style={{ margin: 0, color: effectiveTvMode ? '#f5f5f5' : undefined }}>
            Work order operations
          </Title>
          <Text type="secondary" style={{ color: effectiveTvMode ? '#a6a6a6' : undefined }}>
            {operatorScope
              ? 'Your assigned operations. Start, pause, hold, resume, and complete the work assigned to you.'
              : 'Track operations like a time log: start, pause, hold, resume, and complete.'}
            {isFullscreen ? ' · Press Esc to exit fullscreen' : ''}
          </Text>
        </Col>
        <Col xs={24} md={8} style={{ textAlign: 'right' }}>
          <Space wrap>
            {canEodClose ? (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => navigate('/mfg/work-order-operations/eod-close')}
              >
                End of day
              </Button>
            ) : null}
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                userInitiatedServerFetch.current = true;
                void refetch();
              }}
              loading={isFetching}
            >
              Refresh
            </Button>
            <Button icon={<DesktopOutlined />} onClick={toggleTv} type={tvMode ? 'primary' : 'default'}>
              {tvMode ? 'Exit TV mode' : 'TV mode'}
            </Button>
            <Button
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={toggleFullscreen}
              type={isFullscreen ? 'primary' : 'default'}
            >
              {isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            </Button>
          </Space>
        </Col>
      </Row>

      <div
        className={`mfg-wo-ops-bar${effectiveTvMode ? ' mfg-wo-ops-bar--tv' : ''}`}
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 12,
          background: effectiveTvMode ? undefined : '#fff',
          marginBottom: 12,
          padding: '10px 0 14px',
          borderBottom: effectiveTvMode ? undefined : '1px solid #f0f0f0',
          boxShadow: effectiveTvMode ? undefined : '0 1px 4px rgba(0,0,0,0.04)',
        }}
      >
        <Row align="middle" gutter={[12, 12]}>
          <Col flex="none">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Text type="secondary" style={{ fontSize: 11, margin: 0 }}>
                Shift
              </Text>
              <Progress
                type="circle"
                percent={shiftProgressPercent}
                size={58}
                strokeColor={{ '0%': '#1677ff', '100%': '#52c41a' }}
                format={(p) => <span style={{ fontSize: 12, fontWeight: 600 }}>{p ?? 0}%</span>}
              />
            </div>
          </Col>
          <Col xs={24} lg="auto" flex="1 1 320px">
            <Space wrap>
              <Tag icon={<ClockCircleOutlined />} color="processing">
                Run {boardMetrics.running}
              </Tag>
              <Tag icon={<PauseOutlined />} color="warning">
                Paused {boardMetrics.paused}
              </Tag>
              <Tag icon={<InboxOutlined />} color="orange">
                Hold {boardMetrics.onHold}
              </Tag>
              <Tag icon={<PlayCircleOutlined />} color="success">
                Pending {boardMetrics.pending}
              </Tag>
            </Space>
          </Col>
        </Row>
        <Row align="middle" gutter={[12, 12]} style={{ marginTop: 8 }}>
          <Col xs={24} lg="auto" flex="1 1 320px">
            <Space wrap>
              <Segmented
                size="large"
                value={quickFilter}
                onChange={(v) => setQuickFilter(v as QuickFilter)}
                options={[
                  { label: 'All', value: 'all' },
                  { label: 'Urgent', value: 'urgent' },
                  { label: 'In progress', value: 'in_progress' },
                  { label: 'My line', value: 'my_line' },
                ]}
              />
              <Button
                type="default"
                onClick={() => setDensity((d) => nextBoardDensity(d))}
                title="Tap to cycle density: Compact → Standard → Detail"
                aria-label="Cycle list density, current mode shown on button"
                style={{ minHeight: 48, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                icon={
                  density === 'compact' ? (
                    <AppstoreOutlined />
                  ) : density === 'standard' ? (
                    <UnorderedListOutlined />
                  ) : (
                    <ProfileOutlined />
                  )
                }
              >
                {density === 'compact' ? 'Compact' : density === 'standard' ? 'Standard' : 'Detail'}
              </Button>
              <Button
                type={selectMode ? 'primary' : 'default'}
                icon={<CheckSquareOutlined />}
                onClick={() => {
                  setSelectMode((m) => !m);
                  setSelectedStageKeys(new Set());
                }}
                style={{ minHeight: 48 }}
                title="Select operations for copy or bulk start"
              >
                {selectMode ? 'Exit select' : 'Select'}
              </Button>
              {listFilteredPill && <Tag icon={<FilterOutlined />}>Filtered</Tag>}
              <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                {dataUpdatedAt != null && dataUpdatedAt > 0
                  ? `Data ${formatBoardDataAge(dataUpdatedAt)} · 1m auto-refresh; pull down on touch to sync${
                      isFetching ? ' · syncing' : ''
                    }`
                  : isFetching
                    ? 'Syncing…'
                    : '—'}
              </Text>
              <Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  High contrast
                </Text>
                <Switch checked={highContrast} onChange={setHighContrast} aria-label="High contrast mode" />
              </Space>
            </Space>
          </Col>
          <Col xs={24} md={12} lg={6}>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                allowClear
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />}
                placeholder="Filter this page (ops, item, site…)"
                size="large"
                style={{ minHeight: 48 }}
              />
              <Button
                type="default"
                icon={<AudioOutlined />}
                onClick={startClientSearchVoice}
                title="Voice to filter (browser speech)"
                aria-label="Voice input for page filter"
                style={{ minHeight: 48, minWidth: 48 }}
              />
            </Space.Compact>
          </Col>
        </Row>
        <Row align="bottom" gutter={[12, 12]} style={{ marginTop: 4 }}>
          <Col xs={24} sm={12} md={10} lg={8}>
            <Text type="secondary" style={{ display: 'block', fontSize: 11, marginBottom: 4 }}>
              WO line (this page)
            </Text>
            <Select
              size="large"
              style={{ width: '100%' }}
              value={boardLineFilter}
              onChange={(v) => setBoardLineFilter(String(v))}
              options={boardLineFilterOptions}
              showSearch
              optionFilterProp="label"
            />
          </Col>
          {showBoardUnitFilter ? (
            <>
              <Col xs={24} sm={12} md={10} lg={8}>
                <Text type="secondary" style={{ display: 'block', fontSize: 11, marginBottom: 4 }}>
                  Production unit (Fabrication)
                </Text>
                <Select
                  size="large"
                  style={{ width: '100%' }}
                  value={boardUnitFilter}
                  onChange={(v) => setBoardUnitFilter(String(v))}
                  options={boardUnitFilterOptions}
                />
              </Col>
              <Col xs={24}>
                <WorkOrderExecutionBadge executionScope="Fabrication" size="compact" />
              </Col>
            </>
          ) : (
            <Col xs={24}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Non-Fabrication work orders on this page use BOQ-line operations — complete all line
                operations before delivery (no partial delivery).
              </Text>
            </Col>
          )}
        </Row>
        <Row align="bottom" gutter={[12, 12]} style={{ marginTop: 10 }}>
          <Col xs={24} sm={12} md={10} lg={8}>
            <Text type="secondary" style={{ display: 'block', fontSize: 11, marginBottom: 4 }}>
              Sort work orders
            </Text>
            <Select
              size="large"
              value={boardSort}
              onChange={(v) => setBoardSort(v as BoardSort)}
              options={[
                { value: 'wo_asc', label: 'Work order (A–Z / #)' },
                { value: 'wo_desc', label: 'Work order (Z–A / #)' },
                { value: 'due_asc', label: 'WO due (soonest first)' },
                { value: 'due_desc', label: 'WO due (latest first)' },
                { value: 'customer_asc', label: 'Customer (A–Z)' },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={10} lg={8}>
            <Text type="secondary" style={{ display: 'block', fontSize: 11, marginBottom: 4 }}>
              Group by
            </Text>
            <Select
              size="large"
              value={boardGroup}
              onChange={(v) => setBoardGroup(v as BoardGroup)}
              options={[
                { value: 'none', label: 'None' },
                { value: 'wo_status', label: 'Work order status' },
                { value: 'stage_status', label: 'Current operation (first in sequence)' },
                { value: 'workstation', label: 'Workstation / line' },
              ]}
            />
          </Col>
        </Row>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Fast filters (quick tabs, this-page search, sort, group) run on the data already loaded below."
        description="Use More filters and Search to load work orders from the server (paged, up to 500 per page; default 100). A single client-side list of every work order in the org is not available without a dedicated report/API—use server filters and page through results."
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          include_completed_wo: false,
          hide_skipped: true,
          only_actionable: false,
          search: '',
        }}
        onFinish={onApplyFilters}
      >
        <Card size="small" style={{ marginBottom: 16, borderRadius: 12 }}>
          <Row gutter={[16, 12]} align="middle">
            <Col xs={24} md={10}>
              <Text strong style={{ marginRight: 8 }}>
                Filter by time
              </Text>
              <Radio.Group
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button value="all">All</Radio.Button>
                <Radio.Button value="today">Today</Radio.Button>
                <Radio.Button value="week">This week</Radio.Button>
                <Radio.Button value="month">This month</Radio.Button>
              </Radio.Group>
            </Col>
            <Col xs={24} md={14}>
              <Space wrap>
                <Form.Item name="search" style={{ marginBottom: 0, minWidth: 220, flex: 1 }}>
                  <Input placeholder="Search WO #, customer, project, operation…" allowClear prefix={<SearchOutlined />} />
                </Form.Item>
                <Button type="primary" htmlType="submit">
                  Search
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        <Collapse
          bordered={false}
          style={{ marginBottom: 16, background: 'transparent' }}
          items={[
            {
              key: 'more',
              label: <Text strong>More filters</Text>,
              children: (
                <Card size="small" style={{ borderRadius: 12 }}>
                  <Row gutter={[16, 8]}>
                    <Col xs={24} md={8}>
                      <Form.Item name="work_order_status" label="Work order status">
                        <Select allowClear placeholder="Any" options={WO_STATUS_OPTIONS.map((s) => ({ value: s, label: s }))} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="stage_status" label="Stage status">
                        <Select allowClear placeholder="Any" options={STAGE_STATUS_OPTIONS.map((s) => ({ value: s, label: s }))} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="customer_id" label="Customer">
                        <Select
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          placeholder="Any"
                          loading={customersLoading}
                          options={customerOptions}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="project_id" label="Project">
                        <Select
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          placeholder="Any"
                          loading={projectsLoading}
                          options={projectOptions}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="supervisor_user_id" label="Production supervisor">
                        <Select
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          placeholder="Any"
                          loading={usersLoading}
                          options={userOptions}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="qe_user_id" label="Quality engineer">
                        <Select
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          placeholder="Any"
                          loading={usersLoading}
                          options={userOptions}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="workstation_id" label="Workstation">
                        <Select
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          placeholder="Any workstation"
                          options={workstationFilterOptions}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="due_after" label="WO due from">
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name="due_before" label="WO due by">
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={24}>
                      <Space wrap align="center">
                        <Form.Item name="only_actionable" label="Only actionable" valuePropName="checked" style={{ marginBottom: 0 }}>
                          <Switch />
                        </Form.Item>
                        <Form.Item name="include_completed_wo" label="Include completed WOs" valuePropName="checked" style={{ marginBottom: 0 }}>
                          <Switch />
                        </Form.Item>
                        <Form.Item name="hide_skipped" label="Hide skipped stages" valuePropName="checked" style={{ marginBottom: 0 }}>
                          <Switch />
                        </Form.Item>
                        <Button type="primary" htmlType="submit">
                          Apply filters
                        </Button>
                      </Space>
                    </Col>
                  </Row>
                </Card>
              ),
            },
          ]}
        />
      </Form>

      {isLoading ? (
        <Card loading style={{ borderRadius: 12 }} />
      ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {activeRows.length > 0 &&
            activeGroupSections.map((section) => (
            <div key={`act-${section.key}`} style={{ width: '100%' }}>
              {section.label ? (
                <div style={{ marginBottom: 8 }}>
                  <Text strong type="secondary">
                    {section.label}
                  </Text>
                </div>
              ) : null}
              <div className="mfg-wo-ops-wo-grid">
                {section.items.map(({ wo, stages }) => (
                  <div key={wo.work_order_id} style={{ minWidth: 0 }}>
                    {renderWOCard(wo, stages)}
                  </div>
                ))}
              </div>
            </div>
          ))}
          {completedRows.length > 0 && (
            <Collapse
              bordered={false}
              style={{ background: 'transparent' }}
              defaultActiveKey={[]}
              items={[
                {
                  key: 'completed-wo',
                  label: (
                    <Text strong>
                      Completed today ({completedTodayCount}) — all completed on this view ({completedRows.length} work order
                      {completedRows.length === 1 ? '' : 's'})
                    </Text>
                  ),
                  children: (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                      {completedGroupSections.map((section) => (
                        <div key={`done-${section.key}`} style={{ width: '100%' }}>
                          {section.label ? (
                            <div style={{ marginBottom: 8 }}>
                              <Text strong type="secondary">
                                {section.label}
                              </Text>
                            </div>
                          ) : null}
                          <div className="mfg-wo-ops-wo-grid">
                            {section.items.map(({ wo, stages }) => (
                              <div key={wo.work_order_id} style={{ minWidth: 0 }}>
                                {renderWOCard(wo, stages)}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </Space>
                  ),
                },
              ]}
            />
          )}
          {activeRows.length === 0 && completedRows.length === 0 && (
            <Card>
              <Text type="secondary">No work orders match the current view.</Text>
            </Card>
          )}
        </Space>
      )}

      <Drawer
        title={drawer.wo ? `${drawer.wo.work_order_number}` : 'Work order'}
        placement={drawerNarrow ? 'bottom' : 'right'}
        width={drawerNarrow ? '100%' : 420}
        height={drawerNarrow ? '72vh' : undefined}
        styles={drawerNarrow ? { body: { paddingBottom: 20 } } : undefined}
        open={drawer.open}
        onClose={() => setDrawer({ open: false, wo: null, focusStageId: null })}
        destroyOnClose
        rootClassName="mfg-wo-ops-summary-drawer"
      >
        {drawer.wo && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {operatorScope ? null : (
              <Link to={`/mfg/work-orders/${drawer.wo.work_order_id}`}>
                <Text strong>Open full work order</Text>
              </Link>
            )}
            <div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                Status
              </Text>
              <Tag>
                {drawer.wo.work_order_status === 'IIR Pending'
                  ? 'Job Inspection Pending'
                  : drawer.wo.work_order_status}
              </Tag>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                Customer / project
              </Text>
              <Text>
                {drawer.wo.customer_name ?? '—'}
                {drawer.wo.project_name ? ` · ${drawer.wo.project_name}` : null}
              </Text>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                Job Inspection
              </Text>
              <Text>
                {drawer.wo.iir_status ?? '—'}
                {!drawer.wo.iir_approved && drawer.wo.iir_status ? <Tag color="red">Not approved</Tag> : null}
              </Text>
            </div>
            <Progress percent={drawer.wo.progress_percent} />
            <Text strong>Operations on this work order</Text>
            {drawer.wo.stages.map((s) => (
              <div
                key={s.stage_id}
                style={{
                  padding: 10,
                  borderRadius: 8,
                  border: s.stage_id === drawer.focusStageId ? '1px solid #1677ff' : '1px solid #f0f0f0',
                  background: s.stage_id === drawer.focusStageId ? '#e6f4ff' : undefined,
                }}
              >
                <Space wrap>
                  <Tag color={stageStatusColor(s.status)}>{s.status}</Tag>
                  <Text>{s.operation_label}</Text>
                </Space>
                {s.stage_code ? (
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                    {s.stage_code} · seq {s.sequence_number}
                  </Text>
                ) : null}
              </div>
            ))}
          </Space>
        )}
      </Drawer>

      <Row justify="end" style={{ marginTop: 24 }}>
        <Pagination
          current={filters.page}
          pageSize={filters.page_size}
          total={data?.total_work_orders ?? 0}
          showSizeChanger
          pageSizeOptions={['20', '50', '100', '200', '500']}
          onChange={(page, pageSize) => {
            userInitiatedServerFetch.current = true;
            setFilters((f) => ({
              ...f,
              page,
              page_size: pageSize,
            }));
          }}
          disabled={isLoading}
        />
      </Row>

      {selectMode && (
        <div className="mfg-wo-ops-bulk-bar" role="region" aria-label="Bulk selection actions">
          <Space align="center" wrap style={{ width: '100%', justifyContent: 'space-between' }}>
            <Text strong>
              {selectedStageKeys.size} selected
            </Text>
            <Space wrap>
              <Button onClick={() => setSelectedStageKeys(new Set())} disabled={selectedStageKeys.size === 0}>
                Clear selection
              </Button>
              <Button
                icon={<CopyOutlined />}
                onClick={copySelectedWONumbers}
                disabled={selectedStageKeys.size === 0}
                style={{ minHeight: 48 }}
              >
                Copy WO numbers
              </Button>
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={runBulkStartSelected}
                disabled={selectedStageKeys.size === 0}
                style={{ minHeight: 48 }}
              >
                Start selected
              </Button>
            </Space>
          </Space>
        </div>
      )}
    </div>
  );
};

export default WorkOrderOperationsBoardPage;
